import React, { useEffect } from "react";
import { Box, Theme, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import withAuth from "../../HOC/withAuth";

import {
  ProjectWrapper,
  SectionOneWrapper,
  SectionThreeWrapper,
  SectionFourWrapper,
} from "./styled";

// ✅ Replace this with your actual video path
import owlyDemo from "../../Assets/videos/owly-demo.mp4";

const OwlyCaseStudy = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
  const script = document.createElement("script");
  script.src = "https://t.contentsquare.net/uxa/bb781a00c0c07.js";
  script.async = true;
  document.head.appendChild(script);

  return () => {
    document.head.removeChild(script);
  };
}, []);


  return (
    <ProjectWrapper>
      {/* ===== Blue Circle Page-Load Animation ===== */}
      <Box
        sx={{
          position: "fixed",
          width: "1px",
          height: "1px",
          background: (theme: Theme) => theme.palette.primary.main,
          top: 0,
          left: 0,
          zIndex: 9999999999,
          borderRadius: "100%",
          animation: "animateCircle1 0.5s ease-out",
          animationIterationCount: 1,
          "@keyframes animateCircle1": {
            from: {
              transform: "scale(4000)",
            },
            to: {
              transform: "scale(0)",
            },
          },
        }}
      />

      {/* ===== Heading ===== */}
      <SectionOneWrapper>
        <Typography variant="h1">OWLY</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "flex-start",
            "& p": {
              fontWeight: 500,
              fontSize: { md: "28px", xs: "22px" },
            },
          }}
        >
          <Typography>Energy-aware calendar assistant</Typography>

          {/* CTA */}
          <Box
            component="a"
            href="https://owlyhq.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              background: "#fff",
              border: "4px solid #000",
              px: { md: 4, xs: 3 },
              py: { md: 1.75, xs: 1.25 },
              fontSize: { md: "20px", xs: "16px" },
              fontWeight: 900,
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: "4px 4px 0 #000",
              mt: 3,
              color: "#0021F5",
              textDecoration: "none",
              "&:visited": { color: "#0021F5", textDecoration: "none" },
              "&:hover": {
                background: "#ADFF00",
                transform: "translate(-3px, -3px)",
                boxShadow: "7px 7px 0 #000",
                zIndex: 10,
              },
              "&:active": {
                transform: "translate(2px, 2px)",
                boxShadow: "2px 2px 0 #000",
              },
            }}
          >
            VISIT SITE
          </Box>
        </Box>
      </SectionOneWrapper>

      {/* ===== What is this ===== */}
      <SectionThreeWrapper id="what-is-this">
        <Box>
          <Typography
            variant="h3"
            sx={{ fontSize: { md: "28px", xs: "22px" }, fontWeight: 600 }}
          >
            What is Owly?
          </Typography>
          <Typography sx={{ fontSize: { md: "22px", sm: "18px" }, mt: 2.5 }}>
      Owly is a lightweight layer on top of your calendar that makes it
      <b> energy-aware</b>. It predicts when you’re most alert, protects
      your focus hours, and makes sure demanding meetings don’t clash with
      low-energy periods. The result: schedules that feel natural, not
      draining. <br /><br />
      I vibe-coded Owly to see the potential of AI as a collaborator. 
    </Typography>
        </Box>
      </SectionThreeWrapper>

      {/* ===== Video (local) ===== */}
      <SectionFourWrapper id="video" sx={{ mt: 6 }}>
        <Typography
          variant="h3"
          sx={{ fontSize: { md: "28px", xs: "22px" }, fontWeight: 600, mb: 2 }}
        >
          Product walkthrough
        </Typography>

        <Box
          sx={{
            width: "100%",
            maxWidth: "1040px",
            border: "2px solid #000",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "4px 0px 20px 0px #66666640",
          }}
        >
          <video
            src={owlyDemo}
            controls
            playsInline
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </Box>
      </SectionFourWrapper>

      {/* ===== The problem ===== */}
      <SectionThreeWrapper id="the-problem" sx={{ mt: 8 }}>
        <Box>
          <Typography
            variant="h3"
            sx={{ fontSize: { md: "28px", xs: "22px" }, fontWeight: 600 }}
          >
            The problem
          </Typography>
          <Typography sx={{ fontSize: { md: "22px", sm: "18px" }, mt: 2.5 }}>
            Traditional calendars treat every hour as equal. But in reality,
            energy levels fluctuate through the day, and not all meetings
            require the same cognitive effort. The outcome? <br /> <br />
            • Heavy strategy sessions scheduled during energy slumps. <br />
            • Light, low-stakes meetings blocking peak focus hours. <br />
            • Back-to-back events with no recovery buffers. <br /> <br />
            This mismatch leaves people drained, context-switching constantly,
            and unable to protect their best energy for meaningful work.
          </Typography>
        </Box>
      </SectionThreeWrapper>

      {/* ===== Solution ===== */}
      <SectionFourWrapper id="solution" sx={{ mt: 8 }}>
        <Typography
          variant="h3"
          sx={{ fontSize: { md: "28px", xs: "22px" }, fontWeight: 600 }}
        >
          Solution
        </Typography>

        <Box
          sx={{
            py: 3.5,
            px: 4,
            mt: 3,
            background: (theme: Theme) => theme.palette.secondary.main,
            border: "2px solid #000",
          }}
        >
          <Typography
            sx={{
              fontSize: { md: "22px", xs: "18px" },
              fontWeight: 600,
              lineHeight: "33.89px",
              color: "#000",
            }}
          >
            Owly solves this by combining{" "}
            <b>energy prediction</b> and <b>cognitive load assessment</b>:
            <br />
            <br />
            • <b>Energy prediction:</b> With just two onboarding inputs, Owly
            maps your daily energy curve and predicts your alertness at any
            given hour. <br />
            • <b>Cognitive load scoring:</b> Every meeting is scored for its
            mental demand (ex., deep strategy vs. light status update). <br />
            • <b>Load-to-energy matching:</b> Owly compares event demands to
            predicted energy, nudging or rescheduling mismatched events. <br />
            • <b>Smarter schedules:</b> High-energy hours are protected for deep
            work, lighter tasks fill low-energy zones, and buffers are added for
            recovery. <br />
            <br />
            The result: fewer context switches, less burnout, and schedules that
            adapt to you — not the other way around.
          </Typography>
        </Box>
        <Box
          component="a"
          href="https://owlyhq.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "inline-flex",
            alignSelf: "flex-start",
            background: "#fff",
            border: "4px solid #000",
            px: { md: 4, xs: 3 },
            py: { md: 1.75, xs: 1.25 },
            fontSize: { md: "20px", xs: "16px" },
            fontWeight: 900,
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "4px 4px 0 #000",
            mt: 4,
            color: "#0021F5",
            textDecoration: "none",
            "&:visited": { color: "#0021F5", textDecoration: "none" },
            "&:hover": {
              background: "#ADFF00",
              transform: "translate(-3px, -3px)",
              boxShadow: "7px 7px 0 #000",
              zIndex: 10,
            },
            "&:active": {
              transform: "translate(2px, 2px)",
              boxShadow: "2px 2px 0 #000",
            },
          }}
        >
          VISIT SITE
        </Box>
      </SectionFourWrapper>

      {/* ===== Spacer at bottom ===== */}
      <Box sx={{ height: { md: "120px", xs: "80px" } }} />
    </ProjectWrapper>
  );
};

export default withAuth(OwlyCaseStudy);
